import React, { useState, useEffect } from 'react';
import { X, Clock, User, Package as PackageIcon, AlertCircle } from 'lucide-react';
import axios from 'axios';

/**
 * Modal to display package scan history
 * Shows all status changes with timestamps and who scanned it
 */
function PackageHistoryModal({ isOpen, onClose, deliveryPointId, trackingCode }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen || !deliveryPointId) return;
    
    fetchHistory();
  }, [isOpen, deliveryPointId]);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/packages/history/${deliveryPointId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      setHistory(response.data.history || []);
    } catch (err) {
      console.error('Error fetching package history:', err);
      setError('No se pudo cargar el historial');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'with_driver':
        return 'text-brand-green bg-green-100';
      case 'in_warehouse':
        return 'text-gray-600 bg-gray-100';
      case 'in_fastmax':
        return 'text-blue-600 bg-blue-100';
      case 'out_for_delivery':
        return 'text-purple-600 bg-purple-100';
      case 'delivered':
        return 'text-brand-green bg-green-100';
      case 'returned':
      case 'lost_or_damaged':
        return 'text-red-600 bg-red-100';
      case 'postponed_with_driver':
        return 'text-orange-600 bg-orange-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    const statusMap = {
      'in_warehouse': 'En Almacén',
      'with_driver': 'Con Conductor',
      'in_fastmax': 'En FastMax',
      'out_for_delivery': 'En Ruta',
      'delivered': 'Entregado',
      'returned': 'Devuelto',
      'lost_or_damaged': 'Perdido/Dañado',
      'postponed_with_driver': 'Pospuesto'
    };
    return statusMap[status] || status;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h2 className="text-xl font-montserrat font-bold text-gray-800">
              Historial del Paquete
            </h2>
            {trackingCode && (
              <p className="text-sm text-gray-500 mt-1">
                Código: {trackingCode}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" />
            </div>
          )}

          {error && (
            <div className="flex flex-col items-center justify-center py-12 text-red-600">
              <AlertCircle className="w-12 h-12 mb-4" />
              <p className="font-montserrat">{error}</p>
            </div>
          )}

          {!loading && !error && history.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <PackageIcon className="w-12 h-12 mb-4 text-gray-300" />
              <p className="font-montserrat">No hay historial disponible</p>
            </div>
          )}

          {!loading && !error && history.length > 0 && (
            <div className="space-y-4">
              {history.map((entry, index) => (
                <div
                  key={entry.id || index}
                  className="relative pl-8 pb-6 last:pb-0"
                >
                  {/* Timeline line */}
                  {index !== history.length - 1 && (
                    <div className="absolute left-3 top-8 bottom-0 w-0.5 bg-gray-200" />
                  )}

                  {/* Timeline dot */}
                  <div className={`
                    absolute left-0 top-1.5 w-6 h-6 rounded-full border-4 border-white shadow-md
                    ${getStatusColor(entry.new_status).replace('text-', 'bg-')}
                  `} />

                  {/* Entry Content */}
                  <div className="bg-gray-50 rounded-lg p-3">
                    {/* Status Badge */}
                    <div className="mb-2">
                      <span className={`
                        inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold
                        ${getStatusColor(entry.new_status)}
                      `}>
                        {entry.previous_status && (
                          <span className="opacity-60">
                            {getStatusText(entry.previous_status)} →
                          </span>
                        )}
                        <span>{getStatusText(entry.new_status)}</span>
                      </span>
                    </div>

                    {/* Timestamp */}
                    <div className="flex items-center gap-2 text-xs text-gray-600 mb-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{formatDateTime(entry.timestamp)}</span>
                    </div>

                    {/* Scanned By */}
                    {entry.scanned_by_username && (
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <User className="w-3.5 h-3.5" />
                        <span>Por: {entry.scanned_by_username}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PackageHistoryModal;